import { BoxType, type CustomTemplateBox } from './custom-template';
import type { PsdLayerNode } from './psd-layer-node';

/** Resolves mask provenance for both persisted boxes and legacy drawable identities. */
export function clippingSourceMaskGuard(
  sources: readonly Pick<CustomTemplateBox, 'id' | 'psdLayerId' | 'idFromTemplate'>[],
  originalLayerTree?: readonly PsdLayerNode[],
): (id: string) => boolean {
  const byId = new Map(sources.map(source => [source.id, source]));
  const layerMasks = new Map<string, boolean>();
  const boxMasks = new Map<string, boolean>();
  const visit = (nodes: readonly PsdLayerNode[], inherited = false): void => {
    for (const node of nodes) {
      const masked =
        inherited ||
        !!(node.pixelMask && node.pixelMask.enabled !== false) ||
        !!(node.vectorMask && node.vectorMask.enabled !== false);
      layerMasks.set(node.id, masked);
      if (node.boxRef) boxMasks.set(node.boxRef, masked);
      visit(node.children ?? [], masked);
    }
  };

  visit(originalLayerTree ?? []);
  return (id: string): boolean => {
    const source = byId.get(id);
    // A partial projection cannot establish mask provenance; live-group eligibility rejects missing bases separately.
    if (!source) return false;
    return (
      (source.psdLayerId ? layerMasks.get(source.psdLayerId) : undefined) ??
      boxMasks.get(source.id) ??
      (source.idFromTemplate ? boxMasks.get(source.idFromTemplate) : undefined) ??
      !!source.psdLayerId
    );
  };
}

/** Derives a conservative source-mask guard; the recipe remains the only clipping relationship. */
export function preserveClippingSourceMasks(
  boxes: readonly CustomTemplateBox[],
  originalLayerTree?: readonly PsdLayerNode[],
): CustomTemplateBox[] {
  const sources: CustomTemplateBox[] = [];
  const index = (items: readonly CustomTemplateBox[]): void => {
    for (const box of items) {
      sources.push(box);
      if (box.type === BoxType.GROUP) index(box.boxes);
    }
  };

  index(boxes);
  const requiresStatic = clippingSourceMaskGuard(sources, originalLayerTree);

  const project = (items: readonly CustomTemplateBox[]): CustomTemplateBox[] =>
    items.map(box => {
      if (box.type === BoxType.GROUP) return { ...box, boxes: project(box.boxes) };
      if (box.type !== BoxType.OBJECT) return box;
      const recipe = box.params.compoundClipBakeRecipe;
      if (!recipe || recipe.preserveStaticMask || !recipe.leafBoxIds.some(requiresStatic)) return box;
      return { ...box, params: { ...box.params, compoundClipBakeRecipe: { ...recipe, preserveStaticMask: true } } };
    });
  return project(boxes);
}
